import { useState } from 'react';
import { useFormikContext, type FormikErrors } from 'formik';
import { X } from 'lucide-react';

import { useSource } from '@/react/portainer/gitops/sources/queries/useSource';
import { RefSelector } from '@/react/portainer/gitops/RefField/RefSelector';
import { useGitRefs } from '@/react/portainer/gitops/queries/useGitRefs';
import { SourceId } from '@/react/portainer/gitops/sources/types';

import { TextTip } from '@@/Tip/TextTip';
import { FilePicker } from '@@/form-components/FilePicker/FilePicker';
import { FilePickerSkeleton } from '@@/form-components/FilePicker/FilePickerSkeleton';
import { isDirectory } from '@@/form-components/FilePicker/utils';
import { Widget } from '@@/Widget';
import { CollapseExpandButton } from '@@/CollapseExpandButton';
import { Button } from '@@/buttons';

import { FormValues } from '../../types';
import { isChartDirectory } from '../../chart';
import { ChartOptions } from '../ChartOptions';

import { updateArtifactAt } from './artifactValues';
import { useSourceGitRef } from './useSourceGitRef';
import { useSourceFileTree } from './useSourceFileTree';
import { encodeFileDragId } from './dragId';

export function SourceFilePicker({
  sourceId,
  activeIndex,
  activeFiles,
  onRemove,
}: {
  sourceId: SourceId;
  activeIndex: number;
  activeFiles: FormValues['artifacts'][number]['files'];
  onRemove?: () => void;
}) {
  const { values, initialValues, setValues, errors } =
    useFormikContext<FormValues>();

  // The reconciliation engine has no entry file: it applies whatever its paths
  // resolve to, which may be several files, a directory read the way
  // `kubectl apply -f ./dir` reads one, or a chart. An Edge stack deploys one.
  const isReconciled = values.engine === 'reconcile';
  // Detected while editing too: the update endpoint takes the chart alongside
  // the paths it was detected from, so changing the paths changes the chart.
  const detectsCharts = isReconciled;

  const sourceQuery = useSource(sourceId);
  const refsQuery = useGitRefs({ sourceId });
  const sourceFiles = activeFiles.filter((f) => f.sourceId === sourceId);

  const [isExpanded, setIsExpanded] = useState(true);
  const { gitRef, setGitRef } = useSourceGitRef({
    activeIndex,
    activeRef: sourceFiles[0]?.ref,
    refs: refsQuery.data,
  });
  const {
    fileTree,
    paths,
    isLoading: isFileTreeLoading,
  } = useSourceFileTree({
    sourceId,
    gitRef,
    enabled: !refsQuery.isLoading,
  });

  const isLoading =
    sourceQuery.isLoading || refsQuery.isLoading || isFileTreeLoading;

  // What the artifact was a chart of when the form was loaded, which is what a
  // new selection is measured against.
  const installedChartName = initialValues.artifacts[activeIndex]?.helm.enabled
    ? initialValues.artifacts[activeIndex]?.files[0]?.path
    : '';

  // A chart is a single directory, so a selection of several paths is a set of
  // manifests however many charts it happens to contain.
  const chartPath =
    detectsCharts &&
    sourceFiles.length === 1 &&
    activeFiles.length === 1 &&
    isChartDirectory(paths, sourceFiles[0].path)
      ? sourceFiles[0].path
      : '';

  const replacesChart =
    !!installedChartName && !!chartPath && chartPath !== installedChartName;

  return (
    <Widget>
      <Widget.Taskbar className="mb-0! flex items-center gap-2">
        <CollapseExpandButton
          isExpanded={isExpanded}
          onClick={() => setIsExpanded(!isExpanded)}
        />
        {!sourceQuery.isLoading && <span>{sourceQuery.data?.name}</span>}
        <div className="ml-auto flex items-center gap-2">
          <RefSelector
            inputId={`ref-field-${sourceId}`}
            value={gitRef}
            onChange={(ref) => {
              setGitRef(ref);
              setValues((prev) => {
                return updateArtifactAt(prev, activeIndex, {
                  files: prev.artifacts[activeIndex].files.filter(
                    (f) => f.sourceId !== sourceId
                  ),
                });
              });
            }}
            sourceId={sourceId}
            size="sm"
          />
          {onRemove && (
            <Button
              type="button"
              color="dangerlight"
              size="xsmall"
              icon={X}
              onClick={onRemove}
              aria-label={`Remove ${sourceQuery.data?.name ?? 'source'}`}
              data-cy="workflow-remove-source-button"
            />
          )}
        </div>
      </Widget.Taskbar>
      {isExpanded && (
        <Widget.Body>
          {isLoading && <FilePickerSkeleton showSelectedPanel={false} />}
          {!isLoading && (
            <FilePicker
              files={fileTree}
              onChange={(files) => handleFilesChange(files)}
              value={sourceFiles.map((f) => f.path)}
              // Naming several paths is only useful if they can be seen. An
              // Edge stack lists them itself.
              showSelectedPanel={isReconciled}
              // The server resolves a declared directory itself, so the
              // directory is what it needs to be told.
              selectDirectoryPaths={isReconciled}
              getDraggableId={(path, item) =>
                isDirectory(item)
                  ? undefined
                  : encodeFileDragId({ sourceId, path, ref: gitRef })
              }
            />
          )}

          {/* Helm cannot grow one chart's release into another's: the two name
              their resources differently, so the environment removes the
              release and installs the new chart in its place. Said before
              saving because a file picker is a quieter way to ask for that than
              editing a manifest. */}
          {replacesChart && (
            <TextTip color="orange">
              This replaces the {installedChartName} chart. Saving removes its
              release from every environment and installs the new one, which
              means downtime, and data loss for anything holding data that the
              new chart does not reuse.
            </TextTip>
          )}

          {chartPath && (
            <ChartOptions
              chartPath={chartPath}
              paths={paths}
              namespaceError={helmNamespaceError(errors, activeIndex)}
              value={values.artifacts[activeIndex]?.helm}
              onChange={(helm) =>
                setValues((prev) =>
                  updateArtifactAt(prev, activeIndex, { helm })
                )
              }
            />
          )}
        </Widget.Body>
      )}
    </Widget>
  );

  function handleFilesChange(files: string[]) {
    const artifactFiles = files.map((path) => ({
      sourceId,
      path: path.startsWith('/') ? path.replace('/', '') : path,
      ref: gitRef,
    }));

    const isChart =
      detectsCharts &&
      artifactFiles.length === 1 &&
      isChartDirectory(paths, artifactFiles[0].path);

    setValues((prev) => {
      // Every file of an Edge stack comes from one source, so a selection
      // replaces the lot. A reconciled artifact draws from several at once, so
      // only this source's paths are replaced.
      const kept = isReconciled
        ? (prev.artifacts[activeIndex]?.files ?? []).filter(
            (file) => file.sourceId !== sourceId
          )
        : [];

      return updateArtifactAt(prev, activeIndex, {
        files: [...kept, ...artifactFiles],
        // Detected rather than asked for, and cleared when the selection stops
        // being that chart, so an override or a values file chosen for one
        // chart cannot follow the next selection.
        // The namespace survives a change of chart: it is where the release
        // goes rather than something about the chart. The values files do
        // not, being the old chart's.
        ...(detectsCharts
          ? {
              helm: {
                enabled: isChart,
                valuesFiles: [],
                namespace: prev.artifacts[activeIndex]?.helm.namespace ?? '',
              },
            }
          : {}),
      });
    });
  }
}

/**
 * The namespace's validation message, dug out of Formik's nested errors.
 *
 * The artifact step renders only the errors it knows the shape of, and this
 * one sits two levels down, so without reading it an invalid namespace stops
 * the step with nothing on screen saying why.
 */
function helmNamespaceError(
  errors: FormikErrors<FormValues>,
  index: number
): string | undefined {
  const artifact = errors.artifacts?.[index];

  if (!artifact || typeof artifact === 'string') {
    return undefined;
  }

  const helm = artifact.helm;

  return typeof helm === 'string' ? helm : helm?.namespace;
}
